/**
 * useListPageState
 *
 * Shared list-page state for index routes: sort column/direction plus the
 * standard `filter -> sort -> page` derivation. Filtering and the sort value
 * accessor are supplied by the caller so each entity keeps its own predicate
 * and field typing; this hook owns only the mechanics common to every list.
 */

import { useState } from 'react';
import type { SortDirection } from '../components/shared';

export interface UseListPageStateOptions<T> {
  /** Full, unfiltered result set (e.g. nodes extracted from a GraphQL connection). */
  items: T[];
  /** Predicate applied before sorting. Return true to keep the item. */
  filterFn: (item: T) => boolean;
  /** Current page (1-based) — typically from the route search params. */
  page: number;
  /** Rows per page. */
  pageSize: number;
  /**
   * String value used to compare two items for the active sort column.
   * Callers pass an entity-typed accessor (e.g. `(s, col) => String(s[col as keyof Service] ?? '')`)
   * to keep dynamic field access typed and avoid generic index lookups in the hook.
   */
  sortValue: (item: T, column: string) => string;
  initialSortColumn?: string;
  initialSortDirection?: SortDirection;
}

export interface UseListPageStateResult<T> {
  sortColumn: string;
  sortDirection: SortDirection;
  handleSort: (column: string, direction: SortDirection) => void;
  /** Filtered + sorted items (all pages). */
  filteredItems: T[];
  /** The current page slice of {@link filteredItems}. */
  pagedItems: T[];
  /** Count of filtered items (drives pagination, not the raw item count). */
  totalCount: number;
}

export function useListPageState<T>({
  items,
  filterFn,
  page,
  pageSize,
  sortValue,
  initialSortColumn = 'name',
  initialSortDirection = 'asc',
}: UseListPageStateOptions<T>): UseListPageStateResult<T> {
  const [sortColumn, setSortColumn] = useState<string>(initialSortColumn);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSortDirection);

  const handleSort = (column: string, direction: SortDirection): void => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const filteredItems = items.filter(filterFn).sort((a, b) => {
    if (!sortColumn || !sortDirection) return 0;
    const comparison = sortValue(a, sortColumn).localeCompare(sortValue(b, sortColumn));
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const totalCount = filteredItems.length;
  const startIndex = (page - 1) * pageSize;
  const pagedItems = filteredItems.slice(startIndex, startIndex + pageSize);

  return {
    sortColumn,
    sortDirection,
    handleSort,
    filteredItems,
    pagedItems,
    totalCount,
  };
}
